import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { OAuthService } from "angular-oauth2-oidc";
import { catchError, finalize, map } from "rxjs";
import { environment } from "src/environments/environment";
import { Configuration } from "../domain/configuration";
import { CoreData } from "../tools/core-data/core-data";
import { mapper } from "../tools/mapping/mapper";
import { LoadingTask } from "../tools/ui/loading-task";
import { BaseService } from "./base-service";
import { ConfigurationDTO } from "./dto/configuration.dto";
import { AppConfig } from "../app.config";

@Injectable({
    providedIn: 'root',
})
export class ConfigurationService extends BaseService {

    private serviceUrl = `${this.config.getConfig('serviceBaseUrl')}api/configuration`;

    constructor(public http: HttpClient,
        protected override coreData: CoreData,
        protected override snackBar: MatSnackBar,
        protected override oauthService: OAuthService,
        private loadingTask: LoadingTask,
        protected override router: Router,
        private config: AppConfig
) {
        super(router, coreData, oauthService, snackBar);
    }

    getConfigurationByCode(configCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${configCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getConfigurationByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: ConfigurationDTO) => {
                return mapper.map(response, ConfigurationDTO, Configuration);
            }));
    }

    getLogoByCode(code: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${code}/logo`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getLogoByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    create(conf: Configuration) {

        // const body = JSON.stringify(dto);
        this.loadingTask.activateLoading();

        const formData: FormData = new FormData();

        formData.append('dto', JSON.stringify(conf));
        formData.append('clientLogo', conf.Logo!);

        return this.http.post(`${this.serviceUrl}`, formData, this.generateHeaderForFormData()).pipe(
            catchError(this.handleError<any>('create')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    update(conf: Configuration) {

        this.loadingTask.activateLoading();

        const formData: FormData = new FormData();

        formData.append('dto', JSON.stringify(conf));
        formData.append('clientLogo', conf.Logo!);

        return this.http.put(`${this.serviceUrl}`, formData, this.generateHeaderForFormData()).pipe(
            catchError(this.handleError<any>('update')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    GetMyConfigurationList(onlyActive: boolean = false) {

        this.loadingTask.activateLoading();
        let params = new HttpParams();
        params = params.append('isActive', onlyActive);
        return this.http.get(`${this.serviceUrl}/my`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('GetMyConfigurationList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: ConfigurationDTO[]) => {
                return response.map((item: ConfigurationDTO) => {
                    return mapper.map(item, ConfigurationDTO, Configuration);
                });
            }));
    }

    GetAvailableMasterList() {

        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/master/available`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('GetAvailableMasterList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: ConfigurationDTO[]) => {
                return response.map((item: ConfigurationDTO) => {
                    return mapper.map(item, ConfigurationDTO, Configuration);
                });
            }));
    }

    copyFromMaster(masterConfigCode: string) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${masterConfigCode}/Copy`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('copyFromMaster')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    export(configurationCode: string, filters: string[]) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${configurationCode}/export`, filters, this.generateHeaderForFileDownload()).pipe(
            catchError(this.handleError<any>('export')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    delete(confCode: string) {

        this.loadingTask.activateLoading();
        return this.http.delete(`${this.serviceUrl}/${confCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('delete')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }
}